'use client';

import { useCallback, useEffect } from 'react';

import {
  type IndustrialCaseContentType,
  type IndustrialCaseIdentity,
  trackIndustrialCaseSelection,
  trackIndustrialCaseView,
} from './industrialAnalytics';

/**
 * ### useIndustrialCaseAnalytics
 *
 * Reports the case view once and hands back a click reporter bound to the same
 * case.
 *
 * The landing page discovers its events by walking the DOM for data attributes,
 * because its sections are static markup. A case detail page knows exactly what
 * it renders, so the events are raised where the click happens instead — no
 * attribute contract to keep in sync, and the preview in the admin editor
 * cannot fire them by accident since it never mounts this hook.
 *
 * @param caseIdentity The case being rendered
 * @returns A reporter for clicks on this case's surfaces
 */
export function useIndustrialCaseAnalytics(
  caseIdentity: IndustrialCaseIdentity,
) {
  const { slug, solution, industry } = caseIdentity;

  useEffect(() => {
    trackIndustrialCaseView({ slug, solution, industry });
  }, [industry, slug, solution]);

  /*
   * The reporter is handed down to several components, so it depends on the
   * three identity strings rather than on the object holding them: a caller
   * that builds the identity inline would otherwise produce a new reporter on
   * every render and reattach every handler using it.
   */
  return useCallback(
    (contentType: IndustrialCaseContentType, itemId: string) =>
      trackIndustrialCaseSelection({
        contentType,
        itemId,
        caseIdentity: { slug, solution, industry },
      }),
    [industry, slug, solution],
  );
}

/**
 * ### IndustrialCaseSelectionReporter
 *
 * Reports a click on one of a case's surfaces.
 */
export type IndustrialCaseSelectionReporter = ReturnType<
  typeof useIndustrialCaseAnalytics
>;
